import { AlertTriangle, ExternalLink, RefreshCcw } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import type {
  PaymentReissueEligibility,
  ReissueBlockReason,
  ReissueMethodSnapshot,
} from '@/api/payments';
import { formatDate } from '@/lib/utils';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/** Block reasons that require supplier remediation (method update / approval). */
const SUPPLIER_REVIEW_BLOCK_REASONS: ReadonlySet<ReissueBlockReason> = new Set([
  'METHOD_UNCHANGED',
  'METHOD_NEEDS_REVIEW',
  'NOT_APPROVED',
  'NO_ELIGIBLE_METHOD',
  'DIGITAL_WALLET_NOT_SUPPORTED',
]);

interface ReissuePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eligibility: PaymentReissueEligibility | null;
  submitting: boolean;
  onSubmit: (paymentMethodAccountId: string) => void;
  onOpenSupplier?: () => void;
  onRefreshEligibility?: () => void;
  eligibilityLoading?: boolean;
  supplierName?: string;
}

function methodLabel(method: ReissueMethodSnapshot): string {
  const detail = method.masked_detail ? ` · ${method.masked_detail}` : '';
  return `${method.type}${detail}`;
}

export const ReissuePaymentDialog: React.FC<ReissuePaymentDialogProps> = ({
  open,
  onOpenChange,
  eligibility,
  submitting,
  onSubmit,
  onOpenSupplier,
  onRefreshEligibility,
  eligibilityLoading = false,
  supplierName,
}) => {
  const [selectedMethodId, setSelectedMethodId] = useState<string>('');

  const approvedMethods = eligibility?.approved_methods ?? [];
  const canReissue = eligibility?.can_reissue === true;
  const returnedMethod = eligibility?.returned_method ?? null;
  const returnInfo = eligibility?.return_info;
  const soleApprovedMethodId = approvedMethods.length === 1 ? approvedMethods[0].id : null;

  useEffect(() => {
    if (!open) {
      setSelectedMethodId('');
      return;
    }
    setSelectedMethodId(soleApprovedMethodId ?? '');
  }, [open, soleApprovedMethodId]);

  const blockReason = eligibility?.block_reason ?? null;
  const blockMessage = eligibility?.block_message ?? null;
  const showSupplierWarning =
    !canReissue && !!blockReason && SUPPLIER_REVIEW_BLOCK_REASONS.has(blockReason);
  const showNeutralBlock = !canReissue && !showSupplierWarning;
  const returnDate = returnedMethod?.returned_at ?? returnInfo?.returned_at ?? null;
  const returnCode = returnedMethod?.return_code ?? returnInfo?.return_code ?? null;
  const returnReason = returnedMethod?.return_reason ?? returnInfo?.return_reason ?? null;

  const canSubmit = canReissue && !!selectedMethodId && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCcw className="h-5 w-5" />
            Reissue Payment
          </DialogTitle>
          <DialogDescription>
            Choose which approved payment method to use for the reissue
            {supplierName ? ` to ${supplierName}` : ''}. A new payment file will be generated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                  Returned method
                </p>
                <p className="mt-1 text-sm font-medium text-foreground">
                  {returnedMethod
                    ? methodLabel(returnedMethod)
                    : returnCode
                      ? `ACH return ${returnCode}`
                      : 'Returned payment method'}
                </p>
                {(returnCode || returnReason) && (
                  <p className="mt-0.5 text-xs text-red-700 dark:text-red-300">
                    {returnCode ? `ACH return ${returnCode}` : 'Return'}
                    {returnReason ? ` — ${returnReason}` : ''}
                  </p>
                )}
              </div>
              {returnDate && (
                <span className="shrink-0 rounded-full border border-red-300 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:border-red-800 dark:text-red-300">
                  Returned {formatDate(returnDate)}
                </span>
              )}
            </div>
          </div>

          {showSupplierWarning ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0 space-y-2">
                  <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                    Supplier needs review
                  </p>
                  {blockMessage && (
                    <p className="text-sm text-red-700 dark:text-red-300">{blockMessage}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {onOpenSupplier && (
                      <Button type="button" variant="outline" size="sm" onClick={onOpenSupplier}>
                        Open supplier
                        <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    )}
                    {onRefreshEligibility && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRefreshEligibility}
                        disabled={eligibilityLoading}
                      >
                        <RefreshCcw
                          className={`mr-1.5 h-3.5 w-3.5 ${eligibilityLoading ? 'animate-spin' : ''}`}
                        />
                        Refresh eligibility
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : showNeutralBlock ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="min-w-0 space-y-2">
                  {blockMessage && (
                    <p className="text-sm text-amber-900 dark:text-amber-200">{blockMessage}</p>
                  )}
                  {onRefreshEligibility && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onRefreshEligibility}
                      disabled={eligibilityLoading}
                    >
                      <RefreshCcw
                        className={`mr-1.5 h-3.5 w-3.5 ${eligibilityLoading ? 'animate-spin' : ''}`}
                      />
                      Refresh eligibility
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="reissue-method">Approved payment method</Label>
              <Select value={selectedMethodId} onValueChange={setSelectedMethodId}>
                <SelectTrigger id="reissue-method">
                  <SelectValue placeholder="Select a payment method" />
                </SelectTrigger>
                <SelectContent>
                  {approvedMethods.map((method) => (
                    <SelectItem key={method.id} value={method.id}>
                      {methodLabel(method)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => selectedMethodId && onSubmit(selectedMethodId)}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            {submitting ? 'Reissuing…' : 'Reissue & Generate File'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
